import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  Paper,
  TextField,
  Typography,
  createFilterOptions
} from '@mui/material';
import React, { useCallback, useId, useMemo } from 'react';
import { backgroundColorDarkTheme } from 'styles/global.styles';

interface DropdownSearchbleProps<T extends object, V extends string | number> {
  label: string;
  options: T[];
  value?: V | null;
  onChange: (newValue: V | null) => void;
  labelField: keyof T;
  valueField: keyof T;
  isLoading?: boolean;
  errorMessage?: string;
  placeholder?: string;
  disabled?: boolean;
  optionsLimit?: number;
  getOptionSubLabel?: (option: T) => string;
  getOptionIcon?: (option: T) => React.ReactNode;
}

const CustomPaper = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (props, ref) => (
    <Paper
      {...props}
      ref={ref}
      sx={{
        backgroundColor: backgroundColorDarkTheme,
        border: '1px solid gray',
        color: '#ffffff',
      }}
    />
  )
);
CustomPaper.displayName = 'CustomPaper';

const DropdownSearchbleComponent = <T extends object, V extends string | number>({
  label,
  options = [],
  value,
  onChange,
  labelField,
  valueField,
  isLoading = false,
  errorMessage,
  placeholder,
  disabled = false,
  optionsLimit,
  getOptionSubLabel,
  getOptionIcon,
}: DropdownSearchbleProps<T, V>) => {
  const id = useId();

  const selectedOption = useMemo(
    () => options.find((option) => option[valueField] === value) ?? null,
    [options, value, valueField]
  );

  const filterOptions = useMemo(
    () => createFilterOptions<T>({ limit: optionsLimit }),
    [optionsLimit]
  );

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: T | null) => {
      onChange(newValue ? (newValue[valueField] as V) : null);
    },
    [onChange, valueField]
  );

  const getOptionLabel = useCallback(
    (option: T): string => {
      const val = option[labelField];
      return val === null || val === undefined ? '' : String(val);
    },
    [labelField]
  );

  const currentPlaceholder = useMemo(() => {
    if (!selectedOption) return placeholder ?? 'Selecione uma opção';
    return '';
  }, [selectedOption, placeholder]);

  return (
    <FormControl fullWidth error={!!errorMessage} sx={{ mb: 3 }}>
      <Autocomplete
        loading={isLoading}
        disabled={disabled}
        id={`${id}-autocomplete`}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        filterOptions={filterOptions}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, val) => option[valueField] === val?.[valueField]}
        noOptionsText={isLoading ? 'Carregando...' : 'Nenhuma opção disponível'}
        slots={{ paper: CustomPaper }}
        renderOption={(props, option) => {
          const subLabel = getOptionSubLabel ? getOptionSubLabel(option) : null;
          const icon = getOptionIcon ? getOptionIcon(option) : null;

          return (
            <Box
              component="li"
              {...props}
              key={String(option[valueField])}
              sx={{
                backgroundColor: backgroundColorDarkTheme,
                color: '#ffffff',
                transition: 'background-color 0.2s ease !important',
                '&:hover:not([aria-selected="true"])': {
                  backgroundColor: '#2a2a2a !important',
                },
                '&[aria-selected="true"]': {
                  backgroundColor: '#3a3a3a !important',
                },
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                cursor: 'pointer',
                paddingY: 1,
              }}
            >
              {icon && (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  {icon}
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ width: '100%', fontWeight: 500 }}>
                  {getOptionLabel(option)}
                </Typography>

                {subLabel && (
                  <Typography
                    noWrap
                    variant="caption"
                    sx={{ width: '100%', color: 'gray', mt: 0.5 }}
                  >
                    {subLabel}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => {
          const selectedIcon =
            selectedOption && getOptionIcon ? getOptionIcon(selectedOption) : null;

          return (
          <TextField
            {...params}
            label={label}
            placeholder={currentPlaceholder}
            name={label.replace(/\s+/g, '-').toLowerCase() || `${id}-input`}
            error={!!errorMessage}

            slotProps={{
              inputLabel: {
                sx: { color: '#ffffff', '&.Mui-focused': { color: '#ffffff' } },
              },
              input: {
                ...params.InputProps,
                startAdornment: selectedIcon ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5, mr: -0.5 }}>
                    {selectedIcon}
                  </Box>
                ) : (
                  params.InputProps?.startAdornment
                ),
              },
            }}

            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                borderColor: 'gray',
                '& fieldset': { borderColor: 'gray' },
                '&:hover fieldset': { borderColor: '#ffffff' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
              },
            }}
          />
          );
        }}
        slotProps={{
          clearIndicator: { sx: { color: '#ffffff !important' } },
          popupIndicator: { sx: { color: '#ffffff !important' } },
          listbox: { sx: { maxHeight: '200px' } },
        }}
      />
      {!!errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

export default DropdownSearchbleComponent;